import { initContract } from "@ts-rest/core";
import { z } from "zod";

import {
  AssemblySplitModeSchema,
  FileSchema,
  PartAnalysisSchema,
  PartSchema,
  PartTypeSchema,
  ProcessStepSchema,
  QuoteRequestLineSchema,
} from "../generated/prisma/zod";
import { FormatedFeatureSchema, OperatorSchema, ExtendedPartSchema } from "../types";
import { AnalyzeTypeSchema } from "../types/AnalyzeType";
import { makeUpdateInputSchema } from "../utils/zod-utils/makeUpdateInputSchema";
import type { PyplanContractPart, PycadContractPart } from "../contracts";

const c = initContract();

export const FilterGroupSchema = z.object({
  conditional: z.enum(["AND", "OR"]),
  expressions: z.array(
    z.object({
      id: z.string(),
      property: z.string(),
      operator: OperatorSchema,
      value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
    }),
  ),
});

export const partsContract = c.router({
  findOne: {
    method: "GET",
    path: "/parts/:id",
    responses: {
      200: ExtendedPartSchema.extend({
        files: FileSchema.array(),
      }),
    },
  },
  findOneWithQuoteRequestLines: {
    method: "GET",
    path: "/parts/:id/quote-request-lines",
    responses: {
      200: ExtendedPartSchema.merge(
        z.object({
          files: FileSchema.array(),
          type: PartTypeSchema.nullable(),
          quoteRequestLines: QuoteRequestLineSchema.pick({
            id: true,
            isReliable: true,
          }).array(),
        }),
      ),
    },
  },
  findOneWithChildren: {
    method: "GET",
    path: "/parts/:id/children",
    responses: {
      200: ExtendedPartSchema.merge(
        z.object({
          files: FileSchema.array(),
          type: PartTypeSchema.nullable(),
          children: ExtendedPartSchema.extend({
            children: ExtendedPartSchema.extend({
              files: FileSchema.array(),
              type: PartTypeSchema.nullable(),
            }).array(),
            files: FileSchema.array(),
            type: PartTypeSchema.nullable(),
          }).array(),
        }),
      ),
    },
  },
  search: {
    method: "GET",
    path: "/parts/search",
    query: z.object({
      limit: z.number().optional().default(9),
      search: z.string().optional(),
      filterGroup: FilterGroupSchema,
    }),
    responses: {
      200: ExtendedPartSchema.merge(
        z.object({
          files: FileSchema.array(),
          type: PartTypeSchema.nullable(),

          quoteRequestLines: QuoteRequestLineSchema.merge(
            z.object({
              processSteps: ProcessStepSchema.array(),
            }),
          ).array(),
        }),
      ).array(),
    },
    summary: "Search for parts with filters",
  },
  searchSimilarPart: {
    method: "GET",
    path: "/parts/:id/search",
    query: z.object({
      limit: z.number().optional().default(9),
      search: z.string().optional(),
      filterGroup: FilterGroupSchema,
      shouldReturnChildrenOfAssembly: z.boolean().optional(),
      treeHelperEnabled: z.boolean().optional(),
    }),
    responses: {
      200: ExtendedPartSchema.merge(
        z.object({
          files: FileSchema.array(),
          type: PartTypeSchema.nullable(),

          cosineSimilarity: z.number().optional(),
          quoteRequestLines: QuoteRequestLineSchema.merge(
            z.object({
              processSteps: ProcessStepSchema.array(),
            }),
          ).array(),
        }),
      ).array(),
    },
    summary: "Search for similarParts parts with filters",
  },
  searchTopology: {
    method: "GET",
    path: "/parts/:id/search-topology",
    query: z.object({
      limit: z.number().optional().default(9),
      search: z.string().optional(),
      filterGroup: FilterGroupSchema,
      shouldReturnChildrenOfAssembly: z.boolean().optional(),
      treeHelperEnabled: z.boolean().optional(),
      dimensionWeight: z.number().min(0).max(1).optional().default(0),
    }),
    responses: {
      200: ExtendedPartSchema.merge(
        z.object({
          files: FileSchema.array(),
          type: PartTypeSchema.nullable(),

          cosineSimilarity: z.number().optional(),
          quoteRequestLines: QuoteRequestLineSchema.merge(
            z.object({
              processSteps: ProcessStepSchema.array(),
            }),
          ).array(),
        }),
      ).array(),
    },
    summary: "Search for similarParts parts according to the topology with filters",
  },
  create: {
    method: "POST",
    path: "/parts",
    body: PartSchema.pick({
      name: true,
      type: true,
      parentId: true,
      rootParentId: true,
    })
      .merge(
        PartAnalysisSchema.pick({
          materialGrade: true,
          materialName: true,
          tolerance: true,
          length: true,
          width: true,
          height: true,
        }),
      )
      .extend({
        files: FileSchema.pick({
          id: true,
        }).array(),

        quoteRequestLineId: z.string().nullable().optional(),
        quoteRequestId: z.string().nullable().optional(),
        assemblySplitMode: AssemblySplitModeSchema.nullable().optional(),
        /** @deprecated embeddings computation is disabled; this argument has no effect */
        computeEmbeddings: z.boolean().optional(),
        computeGraphs: z.boolean().optional(),
        computeFeatures: z.boolean().optional(),
        computeTopologicalEmbedding: z.boolean().optional(),
        shouldCreateQuoteRequestLine: z.boolean().optional().default(false),
        index: z.number().optional(),
      })
      .partial()
      .required({ name: true }),
    responses: {
      201: ExtendedPartSchema.merge(
        z.object({
          files: FileSchema.array().nullish(),
          type: PartTypeSchema.nullable(),
        }),
      ),
      403: z.object({
        message: z.string(),
      }),
    },
    summary: "Create a part",
  },
  check: {
    method: "POST",
    path: "/parts/:id/check",
    body: z
      .object({
        force: z.boolean().optional(),
        analyzeType: AnalyzeTypeSchema.optional().default("COMPUTE_ALL"),
        partType: PartTypeSchema.optional(),
        shouldUpdatePartType: z.boolean().optional(),
        /** @deprecated embeddings computation is disabled; this argument has no effect */
        computeEmbeddings: z.boolean().optional(),
        shouldPredictAfterAnalyze: z.boolean().optional(),
        quoteRequestLineId: z.string().optional(),
        assemblySplitMode: AssemblySplitModeSchema.optional(),
      })
      .nullable(),
    responses: {
      200: ExtendedPartSchema.merge(
        z.object({
          files: FileSchema.array(),
          type: PartTypeSchema.nullable(),

          quoteRequestLines: QuoteRequestLineSchema.array(),
          children: ExtendedPartSchema.merge(
            z.object({
              files: FileSchema.array(),
              type: PartTypeSchema.nullable(),
            }),
          ).array(),
        }),
      ),
    },
  },
  findFeatures: {
    method: "GET",
    path: "/parts/:id/features",
    responses: {
      200: z.object({
        features: FormatedFeatureSchema.array(),
        hasUserOverridden: z.boolean(),
      }),
    },
  },
  findBulkFeatures: {
    method: "POST",
    path: "/parts/bulk/features",
    body: z.object({
      partIds: z.array(z.string()),
    }),
    responses: {
      200: z.record(
        z.string(),
        z.object({
          features: FormatedFeatureSchema.array(),
          hasUserOverridden: z.boolean(),
        }),
      ),
    },
  },
  findWeldLength: {
    method: "GET",
    path: "/parts/:id/weld-length",
    responses: {
      200: z.object({ weldLength: z.number().nullable() }),
    },
  },

  updatePartType: {
    method: "PATCH",
    path: "/parts/:id/update-part-type",
    body: z.object({
      quoteRequestLineId: z.string(),
      partType: PartTypeSchema,
      partSubType: z.string().nullable(),
    }),
    responses: {
      200: z.object({
        part: ExtendedPartSchema,
        shouldAnalyzePart: z.boolean(),
        shouldPredictAfterAnalyze: z.boolean(),
      }),
    },
  },

  updatePartSubType: {
    method: "PATCH",
    path: "/parts/:id/update-part-sub-type",
    body: z.object({
      quoteRequestLineId: z.string(),
      partSubType: z.string(),
    }),
    responses: {
      200: z.object({
        part: ExtendedPartSchema,
        hasPredictionBeenTriggered: z.boolean(),
      }),
    },
  },

  updateDimensions: {
    method: "PATCH",
    path: "/parts/:id/update-dimensions",
    body: z.object({
      length: z.number().min(0.01).optional(),
      width: z.number().min(0.01).optional(),
      height: z.number().min(0.01).optional(),
      tubeThickness: z.number().min(0.01).optional(),
      quoteRequestLineId: z.string(),
    }),
    responses: {
      200: ExtendedPartSchema,
    },
  },

  updateWeight: {
    method: "PATCH",
    path: "/parts/:id/update-weight",
    body: z.object({
      weight: z.number().min(0.01),
      quoteRequestLineId: z.string(),
    }),
    responses: {
      200: ExtendedPartSchema,
    },
  },

  updatePart: {
    method: "PATCH",
    path: "/parts/:id",
    body: makeUpdateInputSchema(
      PartSchema.omit({ id: true, createdAt: true }).merge(
        PartAnalysisSchema.pick({
          quantityInAssembly: true,
          weight: true,
          height: true,
          length: true,
          width: true,
          tubeThickness: true,
        }),
      ),
    ).extend({
      formattedFeaturesJsonOverride: FormatedFeatureSchema.array().nullish(),
    }),
    responses: {
      200: ExtendedPartSchema,
    },
  },

  getPartJson: {
    method: "GET",
    path: "/parts/:id/json",
    responses: {
      200: z.custom<PyplanContractPart | PycadContractPart | null>(),
    },
  },
});
